// sapPin.js
// -----------------------------------------------------------------------------
// Writes a พิกัด / จุดสังเกต someone edited in this app back to the SO in SAP
// (U_KSK_Geolocation / U_KSK_Landmark), through the ASMX UpdateSoPin method --
// so the next GetSo, on any page, reads what was last typed.
//
// Always called AFTER the Firestore save has succeeded, and never throws: a job
// with the right pin in Firestore is what the driver needs, and SAP being down
// must not undo or block that. The caller is told which SOs failed, so it can
// say so instead of implying SAP was updated.
//
// `patch` comes from R.sapPinPatch: null for a field left alone, a string --
// '' to clear -- for one that changed. Only changed fields are sent, so an edit
// of the landmark cannot overwrite a pin someone typed in SAP meanwhile.
// -----------------------------------------------------------------------------
import axios from 'axios'
import R from '@/components/bookingRules'
import { reportError } from '@/components/report'

export async function pushPinToSap (webPath, soNos, patch) {
  const failed = []
  if (!patch) return failed
  for (const so of R.sapSoNumbers(soNos)) {
    try {
      const res = await axios.post(`${webPath}deliveryWebService.asmx/UpdateSoPin`,
        { so: Number(so), geo: patch.geo, landmark: patch.landmark }, { timeout: 10000 })
      if (!res || !res.data || res.data.d !== 'true') throw new Error('UpdateSoPin returned ' + (res && res.data && res.data.d))
    } catch (e) {
      reportError(`UpdateSoPin failed for SO ${so}:`, e)
      failed.push(so)
    }
  }
  return failed
}

// UpdateRtPin: a return-only pickup's pin lives on its RT (ORDN).
export async function pushRtPinToSap (webPath, rtNos, patch) {
  const failed = []
  if (!patch) return failed
  for (const rt of R.sapSoNumbers(rtNos)) {
    try {
      const res = await axios.post(`${webPath}deliveryWebService.asmx/UpdateRtPin`,
        { rt: Number(rt), geo: patch.geo, landmark: patch.landmark }, { timeout: 10000 })
      if (!res || !res.data || res.data.d !== 'true') throw new Error('UpdateRtPin returned ' + (res && res.data && res.data.d))
    } catch (e) {
      reportError(`UpdateRtPin failed for RT ${rt}:`, e)
      failed.push('RT ' + rt)
    }
  }
  return failed
}

// Where a booking's pin goes in SAP: the document the booking came from. An
// exchange's customer is the SO's; a pickup has only its RT.
export function pinTargets (b) {
  const x = b || {}
  if (x.jobType === 'return') return { soNos: [], rtNos: [x.rtNo] }
  return { soNos: [x.soNo], rtNos: [] }
}

// Both writes in one call, for the views.
export async function pushBookingPinToSap (webPath, b, patch) {
  const t = pinTargets(b)
  const [a, c] = await Promise.all([pushPinToSap(webPath, t.soNos, patch), pushRtPinToSap(webPath, t.rtNos, patch)])
  return a.concat(c)
}

// The one Thai line both pages show when SAP was not updated.
export function sapFailedNote (failed) {
  return failed.length
    ? `บันทึกในระบบแล้ว แต่ยังอัปเดตพิกัด/จุดสังเกตใน SAP ไม่สำเร็จ (${failed.map(n => /^RT /.test(n) ? n : 'SO ' + n).join(', ')}) โปรดแก้ไขใน SAP`
    : ''
}
